import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, ROOMS } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowLeft, Users, Globe, LayoutGrid, PenLine, UserPlus, Mail, Heart } from "lucide-react";
import UsernameClaimModal from "@/components/UsernameClaimModal";
import WritePostModal from "@/components/WritePostModal";
import PostCard from "@/components/PostCard";

/**
 * The Commons — social layer. Strictly chronological (newest first),
 * no ranking, no popularity counts anywhere in this file or its API calls.
 * Tabs: Following · Everyone · Browse by shelf. Phase (a) ships the door,
 * route, tabs, and empty states; Phase (b) will add the posting flow.
 */
const TABS = [
  { key: "following", label: "Friends",  Icon: Users,      testId: "commons-tab-following" },
  { key: "everyone",  label: "Everyone", Icon: Globe,      testId: "commons-tab-everyone" },
  { key: "browse",    label: "Browse",   Icon: LayoutGrid, testId: "commons-tab-browse" },
];

// Media types we let people filter by on the Browse tab. Mirrors ROOMS minus
// commons itself. Kept as a stable list here so the filter UI order doesn't
// drift with the Hall room order.
const SHELF_FILTERS = ROOMS.filter(r => !r.social).map(r => ({
  type: r.type,
  name: r.name,
}));

export default function Commons() {
  const [sp, setSp] = useSearchParams();
  const tab = TABS.find(t => t.key === sp.get("tab"))?.key || "following";
  const shelf = sp.get("shelf") || "";
  const { user } = useAuth();

  const [posts, setPosts] = useState([]);
  const [nextBefore, setNextBefore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [showClaim, setShowClaim] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [showWrite, setShowWrite] = useState(false);

  // On first Commons visit, fetch the caller's profile. If they haven't
  // claimed a username yet, pop the claim modal — mandatory before doing
  // anything social. Immutable after set (backend enforces).
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await api.get("/commons/profile/me");
        if (cancel) return;
        setProfile(r.data);
        if (!r.data.username) setShowClaim(true);
      } catch { /* ignore — auth guard elsewhere */ }
    })();
    return () => { cancel = true; };
  }, []);

  // Poll (once per Commons visit — not on a timer, per the "no notifications"
  // ethos) the incoming request count so the mail-slot pill can show a quiet
  // number. Deliberately NOT a live/websocket count; deliberately no toast.
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await api.get("/commons/friend-requests", { params: { direction: "incoming" } });
        if (!cancel) setPendingCount((r.data.requests || []).length);
      } catch { /* ignore */ }
    })();
    return () => { cancel = true; };
  }, []);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    setPosts([]);
    setNextBefore(null);
    (async () => {
      try {
        const params = { tab };
        if (tab === "browse" && shelf) params.type = shelf;
        const r = await api.get("/commons/posts", { params });
        if (cancel) return;
        setPosts(r.data.posts || []);
        setNextBefore(r.data.next_before || null);
      } catch (e) {
        console.error(`[commons:${tab}] posts load failed:`, e);
        if (!cancel) setPosts([]);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [tab, shelf]);

  async function loadOlder() {
    if (!nextBefore) return;
    try {
      const params = { tab, before: nextBefore };
      if (tab === "browse" && shelf) params.type = shelf;
      const r = await api.get("/commons/posts", { params });
      setPosts(cur => [...cur, ...(r.data.posts || [])]);
      setNextBefore(r.data.next_before || null);
    } catch { /* ignore */ }
  }

  function setTab(nextTab) {
    const next = new URLSearchParams(sp);
    next.set("tab", nextTab);
    if (nextTab !== "browse") next.delete("shelf");
    setSp(next, { replace: true });
  }

  function setShelf(nextShelf) {
    const next = new URLSearchParams(sp);
    next.set("tab", "browse");
    if (nextShelf) next.set("shelf", nextShelf); else next.delete("shelf");
    setSp(next, { replace: true });
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-8 pt-8 pb-24" data-testid="commons">
      <Link to="/" data-testid="back-to-rooms" className="inline-flex items-center gap-2 font-mono-lc text-[10px] tracking-[0.24em] uppercase text-[var(--cream-mute)] hover:text-[var(--brass)] transition-colors">
        <ArrowLeft size={12} strokeWidth={1.5} />
        back to rooms
      </Link>

      <header className="text-center mt-8 sm:mt-10">
        <p className="font-mono-lc text-[10px] tracking-[0.24em] uppercase text-[var(--brass)] opacity-80">
          the commons
        </p>
        <h1 className="mt-3 font-serif-lit text-3xl sm:text-5xl text-[var(--cream)] leading-[1.05]">
          taste, not algorithms
        </h1>
        <p className="mt-3 font-serif-lit italic text-[var(--cream-dim)] text-base sm:text-lg">
          chronological. your friends, your shelves, your words.
        </p>
      </header>

      {/* tabs — full-width on mobile so tap targets stay ≥44px */}
      <div className="mt-8 sm:mt-10 flex items-stretch gap-1 sm:gap-2 border border-[rgba(179,137,90,0.16)] rounded-full p-1 bg-[var(--bg-panel)]" role="tablist" aria-label="Commons feeds">
        {TABS.map(({ key, label, Icon, testId }) => {
          const active = tab === key;
          return (
            <button
              key={key}
              role="tab"
              aria-selected={active}
              onClick={() => setTab(key)}
              data-testid={testId}
              className={
                "flex-1 inline-flex items-center justify-center gap-1.5 py-2.5 rounded-full transition-colors " +
                (active
                  ? "bg-[var(--brass)] text-[var(--bg)]"
                  : "text-[var(--cream-dim)] hover:text-[var(--brass)]")
              }
            >
              <Icon size={13} strokeWidth={1.6} />
              <span className="font-mono-lc text-[10px] sm:text-[11px] tracking-[0.16em] uppercase">
                {label}
              </span>
            </button>
          );
        })}
      </div>

      {/* Browse-by-shelf chips (only on Browse tab) */}
      {tab === "browse" && (
        <div className="mt-4 flex flex-wrap gap-2" data-testid="commons-shelf-filters">
          <button
            onClick={() => setShelf("")}
            data-testid="commons-shelf-all"
            className={`chip ${!shelf ? "active" : ""}`}
          >
            all shelves
          </button>
          {SHELF_FILTERS.map(({ type, name }) => (
            <button
              key={type}
              onClick={() => setShelf(type)}
              data-testid={`commons-shelf-${type}`}
              className={`chip ${shelf === type ? "active" : ""}`}
            >
              {name.toLowerCase()}
            </button>
          ))}
        </div>
      )}

      {/* Action row — always visible, one tap to write, browse people, or check requests */}
      <div className="mt-6 flex flex-wrap justify-center gap-3">
        <button
          type="button"
          onClick={() => setShowWrite(true)}
          data-testid="commons-write-btn"
          className="lc-btn inline-flex items-center gap-2"
        >
          <PenLine size={12} strokeWidth={1.8} />
          write a post
        </button>
        <Link
          to="/commons/directory"
          data-testid="commons-find-people-btn"
          className="lc-btn-ghost inline-flex items-center gap-2"
        >
          <UserPlus size={12} strokeWidth={1.6} />
          find people
        </Link>
        <Link
          to="/commons/friends"
          data-testid="commons-friends-btn"
          className="lc-btn-ghost inline-flex items-center gap-2"
        >
          <Heart size={12} strokeWidth={1.6} />
          friends
        </Link>
        <Link
          to="/commons/requests"
          data-testid="commons-requests-btn"
          className="lc-btn-ghost inline-flex items-center gap-2"
        >
          <Mail size={12} strokeWidth={1.6} />
          requests
          {pendingCount > 0 && (
            <span
              data-testid="commons-requests-count"
              className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[var(--brass)] text-[var(--bg)] font-mono-lc text-[10px] tracking-normal"
            >
              {pendingCount}
            </span>
          )}
        </Link>
      </div>

      {/* Feed */}
      <section className="mt-8" data-testid="commons-feed">
        {loading ? (
          <p className="text-center font-mono-lc text-[10px] tracking-[0.24em] uppercase text-[var(--cream-mute)]" data-testid="commons-loading">
            opening the door…
          </p>
        ) : posts.length === 0 ? (
          <EmptyState tab={tab} />
        ) : (
          <ul className="space-y-4">
            {posts.map(p => (
              <li key={p.id}>
                <PostCard
                  post={p}
                  isOwner={user?.user_id === p.user_id}
                  currentUserId={user?.user_id}
                  onDeleted={(id) => setPosts(cur => cur.filter(x => x.id !== id))}
                />
              </li>
            ))}
          </ul>
        )}

        {nextBefore && !loading && (
          <div className="mt-8 flex justify-center">
            <button
              onClick={loadOlder}
              data-testid="commons-load-older"
              className="lc-btn-ghost"
            >
              load older posts
            </button>
          </div>
        )}
      </section>

      {showClaim && (
        <UsernameClaimModal
          onClaimed={(p) => { setProfile(p); setShowClaim(false); }}
        />
      )}

      {showWrite && (
        <WritePostModal
          onClose={() => setShowWrite(false)}
          onPosted={() => {
            setShowWrite(false);
            // Bounce to the everyone feed and reload so the new post shows.
            const next = new URLSearchParams(sp);
            next.set("tab", "everyone");
            setSp(next, { replace: true });
          }}
        />
      )}
    </div>
  );
}

function EmptyState({ tab }) {
  const copy = useMemo(() => {
    if (tab === "following") return {
      title: "your friends haven't posted yet",
      body: "when a friend shares something they love, it'll land here, newest first.",
    };
    if (tab === "browse") return {
      title: "no posts on this shelf yet",
      body: "pick a shelf above, or share something from your own library to get the conversation going.",
    };
    return {
      title: "the commons is quiet right now",
      body: "share the first thing you love — your post will land right here.",
    };
  }, [tab]);
  return (
    <div className="text-center py-12" data-testid="commons-empty">
      <p className="font-serif-lit text-xl sm:text-2xl text-[var(--cream)]">{copy.title}</p>
      <p className="mt-3 font-garamond italic text-[var(--cream-dim)] max-w-md mx-auto">{copy.body}</p>
    </div>
  );
}
